import 'react-loading-skeleton/dist/skeleton.css'

import { Document, Page, View } from '../../compiler'

import trackEvents from '../../../../../constants/track-events'
import { useDiscover } from '../../../../../features/discover/hooks/useDiscover'
import {
  MESSAGE_ROLES,
  MESSAGE_TYPES
} from '../../../../../features/discover/utils'
import { trackEvent } from '../../../../../helpers/analytics'
import { isNumber } from '../../utils'
import {
  getDimensionExplanation,
  hasRankedDimensions,
  isCultureLong
} from '../culture/culture-section'
import CultureRankedList from '../culture/CultureRankedList'
import CategoryBadges from './CategoryBadges'
import CultureFitContainer from './CultureFitContainer'
import DimensionCard from './DimensionCard'
import TalentProfileLoading from './loading'
import styles, { CATEGORY_BADGES_WIDTH_PERCENT } from './styles'
import TitleDescription from './TitleDescription'

const TalentProfile = ({
  data,
  withoutAttachments,
  isLoading,
  hasCompletedOnboarding = true
}) => {
  const {
    enableAssistant,
    setAssistantIsTyping,
    sendMessageToAssistant,
    setAssistantFocus
  } = useDiscover()

  const {
    skills_section = { data: [], title: '', description: '' },
    personality_section = { data: [], title: '', description: '' },
    culture_section = { title: '', description: '' },
    interests_section = { data: [], title: '', description: '' },
    overview_section = {
      title: '',
      description: '',
      skills: { title: '', data: [] },
      personality: { title: '', data: [] },
      culture: { title: '', data: [] },
      interests: { title: '', data: [] }
    }
  } = data || {}

  /* Kultur im short mode sind die 2-Achsen-Quadranten (CultureFitContainer),
     im long mode die Werte-Rangliste des Talents (CultureRankedList, geteilt
     mit dem Anforderungsprofil). Payloads ohne mode sind legacy und damit
     short. */
  const is_culture_long = isCultureLong(culture_section)

  const has_short_culture_data =
    isNumber(culture_section?.data?.talent_scores?.score_coopcomp) &&
    isNumber(culture_section?.data?.talent_scores?.score_orderchaos)

  const has_long_culture_data = hasRankedDimensions(culture_section)

  const is_culture_available = is_culture_long
    ? has_long_culture_data
    : has_short_culture_data

  /* auf die Daten gaten, die das jeweilige Objekt tatsächlich liest. Im long
     mode gibt es kein culture_section.data und umgekehrt keine
     ranked_dimensions */
  const shortCultureProps = has_short_culture_data
    ? {
        title: culture_section.data.title,
        userCultureScores: {
          HEROS_JOURNEY_score_coopcomp:
            culture_section.data.talent_scores.score_coopcomp,
          HEROS_JOURNEY_score_orderchaos:
            culture_section.data.talent_scores.score_orderchaos
        },
        insights: culture_section.data.insights
      }
    : null

  /* score_title und insights liest die Karte wie im PDF nicht. Der Kartenkopf
     wiederholte die Seitenüberschrift, und die Insights benannten Rang 1-3 ein
     zweites Mal, die stehen ausführlich in der Top-3-Bande. */
  const longCultureProps = has_long_culture_data
    ? {
        data: culture_section.ranked_dimensions,
        topDimensions: culture_section.top_dimensions
      }
    : null

  const is_skills_available =
    !!skills_section.data && !!skills_section.data.length
  const is_personality_available =
    !!personality_section.data && !!personality_section.data.length
  const is_interests_available =
    !!interests_section.data && !!interests_section.data.length

  const pageProps = {
    // size: getPageSize(PDF_DPI_NUMBER),
    // wrap: true,
    style: styles.pageContainer
  }

  const sendDescriptionToAssistant = (description) => {
    trackEvent(trackEvents.DISCOVER_CLICK_TALENT_PROFILE_DIMENSION_TITLE)
    enableAssistant()
    setAssistantIsTyping(true)
    setTimeout(() => {
      setAssistantIsTyping(false)
      sendMessageToAssistant({
        id: 1,
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.TEXT,
        description
      })
      setAssistantFocus(false)
    }, 1000)
  }

  const sendCultureDimensionToAssistant = (dimension) => {
    if (!hasCompletedOnboarding) return
    sendDescriptionToAssistant(getDimensionExplanation(dimension))
  }

  if (isLoading) {
    return <TalentProfileLoading />
  }

  return (
    <Document>
      {/** HEADER + SUMMARY */}
      <Page>
        <View>
          <TitleDescription
            title={overview_section.title}
            description={overview_section.description}
          />
          <View
            id='talent_profile_summary'
            style={styles.categoryBadgesContainer}
          >
            {[
              overview_section.skills,
              overview_section.personality,
              overview_section.culture,
              overview_section.interests
            ]
              .filter((category) => !!category?.data?.length)
              .map(({ title, data }, index) => (
                <CategoryBadges
                  key={index}
                  style={{ width: CATEGORY_BADGES_WIDTH_PERCENT }}
                  title={title}
                  facts={data}
                />
              ))}
          </View>
        </View>
      </Page>
      {/** SKILLS */}
      {is_skills_available && (
        <Page {...pageProps}>
          <TitleDescription
            title={skills_section.title}
            description={skills_section.description}
          />
          <View style={{ marginTop: 24 }} className='flex flex-col gap-y-4'>
            {skills_section.data.map((d, index) => {
              const {
                title,
                talent_score,
                endpole_left,
                endpole_right,
                insight_description,
                description
              } = d
              return (
                <DimensionCard
                  key={index}
                  title={title}
                  talentScore={talent_score}
                  endPoleLeftTitle={endpole_left}
                  endPoleRightTitle={endpole_right}
                  insightDescription={insight_description}
                  hasCompletedOnboarding={hasCompletedOnboarding}
                  onClickDimensionTitle={() => {
                    if (!hasCompletedOnboarding) return
                    sendDescriptionToAssistant(description)
                  }}
                />
              )
            })}
          </View>
        </Page>
      )}

      {/** PERSONALITY */}
      {is_personality_available && (
        <Page {...pageProps}>
          <TitleDescription
            title={personality_section.title}
            description={personality_section.description}
          />
          <View style={{ marginTop: 24 }} className='flex flex-col gap-y-4'>
            {personality_section.data.map((d, index) => {
              const {
                title,
                talent_score,
                endpole_left,
                endpole_right,
                insight_description,
                description
              } = d
              return (
                <DimensionCard
                  key={index}
                  title={title}
                  talentScore={talent_score}
                  endPoleLeftTitle={endpole_left}
                  endPoleRightTitle={endpole_right}
                  insightDescription={insight_description}
                  hasCompletedOnboarding={hasCompletedOnboarding}
                  onClickDimensionTitle={() => {
                    if (!hasCompletedOnboarding) return
                    sendDescriptionToAssistant(description)
                  }}
                />
              )
            })}
          </View>
        </Page>
      )}

      {/** CULTURE */}
      {is_culture_available && (
        <Page {...pageProps}>
          <TitleDescription
            title={culture_section.title}
            description={culture_section.description}
          />
          {is_culture_long ? (
            <CultureRankedList
              containerStyle={{ marginTop: 24, marginHorizontal: 24 }}
              withColumnHeader
              hasCompletedOnboarding={hasCompletedOnboarding}
              onClickDimension={sendCultureDimensionToAssistant}
              {...longCultureProps}
            />
          ) : (
            <CultureFitContainer
              containerStyle={{ marginTop: 24, marginHorizontal: 24 }}
              {...shortCultureProps}
            />
          )}
        </Page>
      )}

      {/** INTERESTS */}
      {is_interests_available && (
        <Page {...pageProps}>
          <TitleDescription
            title={interests_section.title}
            description={interests_section.description}
          />
          <View style={{ marginTop: 24 }} className='flex flex-col gap-y-4'>
            {interests_section.data.map((d, index) => {
              const {
                title,
                talent_score,
                endpole_left,
                endpole_right,
                insight_description,
                description
              } = d
              return (
                <DimensionCard
                  key={index}
                  title={title}
                  talentScore={talent_score}
                  endPoleLeftTitle={endpole_left}
                  endPoleRightTitle={endpole_right}
                  insightDescription={insight_description}
                  hasCompletedOnboarding={hasCompletedOnboarding}
                  onClickDimensionTitle={() => {
                    if (!hasCompletedOnboarding) return
                    sendDescriptionToAssistant(description)
                  }}
                />
              )
            })}
          </View>
        </Page>
      )}

      {/** APPENDIX - SKILLS */}
      {is_skills_available && !withoutAttachments && (
        <Page {...pageProps}>
          <TitleDescription
            title={skills_section.title}
            description={skills_section.description}
          />
          <View style={{ marginTop: 24 }} className='flex flex-col gap-y-4'>
            {skills_section.data.map((d, index) => {
              const {
                title,
                description,
                talent_score,
                endpole_left,
                endpole_right,
                insight_description
              } = d
              return (
                <DimensionCard
                  key={index}
                  title={title}
                  description={description}
                  talentScore={talent_score}
                  endPoleLeftTitle={endpole_left}
                  endPoleRightTitle={endpole_right}
                  insightDescription={insight_description}
                  hasCompletedOnboarding={hasCompletedOnboarding}
                />
              )
            })}
          </View>
        </Page>
      )}

      {/** APPENDIX - PERSONALITY */}
      {is_personality_available && !withoutAttachments && (
        <Page {...pageProps}>
          <TitleDescription
            title={personality_section.title}
            description={personality_section.description}
          />
          <View style={{ marginTop: 24 }} className='flex flex-col gap-y-4'>
            {personality_section.data.map((d, index) => {
              const {
                title,
                description,
                talent_score,
                endpole_left,
                endpole_right,
                insight_description
              } = d
              return (
                <DimensionCard
                  key={index}
                  title={title}
                  description={description}
                  talentScore={talent_score}
                  endPoleLeftTitle={endpole_left}
                  endPoleRightTitle={endpole_right}
                  insightDescription={insight_description}
                  hasCompletedOnboarding={hasCompletedOnboarding}
                />
              )
            })}
          </View>
        </Page>
      )}

      {/** APPENDIX - CULTURE  */}
      {is_culture_available && !withoutAttachments && (
        <Page {...pageProps}>
          <TitleDescription
            title={culture_section.title}
            description={culture_section.description}
          />
          {/* Ohne Top-3-Bande, die steht schon auf der Kulturseite. Der
              Anhang zeigt dafür alle zehn Ränge ab 01. */}
          {is_culture_long ? (
            <CultureRankedList
              containerStyle={{ marginTop: 24, marginHorizontal: 24 }}
              withColumnHeader
              withTopThree={false}
              {...longCultureProps}
            />
          ) : (
            <CultureFitContainer
              containerStyle={{ marginTop: 24, marginHorizontal: 24 }}
              {...shortCultureProps}
            />
          )}
        </Page>
      )}

      {/** APPENDIX - INTERESTS */}
      {is_interests_available && !withoutAttachments && (
        <Page {...pageProps}>
          <TitleDescription
            title={interests_section.title}
            description={interests_section.description}
          />
          <View style={{ marginTop: 24 }} className='flex flex-col gap-y-4'>
            {interests_section.data.map((d, index) => {
              const {
                title,
                talent_score,
                endpole_left,
                endpole_right,
                description,
                insight_description
              } = d
              return (
                <DimensionCard
                  key={index}
                  description={description}
                  title={title}
                  talentScore={talent_score}
                  endPoleLeftTitle={endpole_left}
                  endPoleRightTitle={endpole_right}
                  insightDescription={insight_description}
                  hasCompletedOnboarding={hasCompletedOnboarding}
                />
              )
            })}
          </View>
        </Page>
      )}
    </Document>
  )
}

export default TalentProfile
